/**
 * useVoiceRecorder — MediaRecorder + AnalyserNode wrapper that drives
 * the omnibar's voice-note recording flow.
 *
 * Responsibilities:
 *
 *   1. Acquire the microphone (`navigator.mediaDevices.getUserMedia`)
 *      and wire it into a fresh AudioContext so the same input stream
 *      feeds both the MediaRecorder (which assembles the final blob)
 *      and an AnalyserNode (which feeds the live amplitude meter).
 *   2. Sample the analyser at animation-frame cadence to drive the
 *      `liveLevels` array — a rolling window of recent amplitudes the
 *      input-row live waveform reads off as a visual signal that "the
 *      mic is hearing something".
 *   3. Sample the analyser at a coarser cadence (every 100ms) into a
 *      growing `peaks` array — the source of truth for the final
 *      voice chip's compact waveform preview. Downsampled to a fixed
 *      bar count (`PEAK_BAR_COUNT`) on stop so a 2-second note and a
 *      2-minute note both produce a chip with the same number of bars.
 *   4. On stop, emit `(file, peaks, durationSeconds)` through the
 *      consumer-supplied `onComplete` callback. The file uses the
 *      browser's chosen MIME container — Safari hands back
 *      `audio/mp4`, Chrome / Firefox `audio/webm`. The server-side
 *      audio handler accepts both (see
 *      `production/api/src/services/ingestor/handlers/audio.ts`).
 *   5. On cancel, tear the stream down and emit nothing — the recording
 *      is discarded. This is the only path that drops audio without a
 *      side effect; every other completion path goes through
 *      `onComplete`.
 *
 * Lifecycle:
 *
 *   • `start()` — async; rejects if the user denies microphone
 *     permission or the browser lacks `getUserMedia`/`MediaRecorder`.
 *     The caller is expected to surface the failure (the hook itself
 *     can't show toasts without coupling to a notification system).
 *   • `stop()` — synchronous; the actual blob assembly + `onComplete`
 *     callback fires asynchronously when `MediaRecorder.onstop` runs.
 *   • `cancel()` — synchronous; tears down the same as `stop()` but
 *     suppresses the completion callback.
 *   • Component unmount — fires `cancel()` so a fast nav away from the
 *     omnibar host can't leave the browser's mic indicator lit.
 *
 * Reasonable scope: the hook does NOT debounce, does NOT cap maximum
 * recording duration (the server enforces its own ceiling), and does
 * NOT auto-stop on silence. Those are policy decisions for the
 * caller to layer on top.
 */
import { useCallback, useEffect, useRef, useState } from "react";
/**
 * Number of bars surfaced in the live amplitude meter that overlays
 * the input row while recording. The meter is a rolling window — every
 * new amplitude sample shifts the array left and pushes a new value
 * onto the right, so the bars appear to scroll horizontally without
 * needing per-bar animation logic in the renderer.
 */
const LIVE_BAR_COUNT = 48;
/**
 * Number of bars stored in the final voice chip's compact waveform
 * preview. Independent of recording duration — `peaks` is downsampled
 * (max-bucketed) on stop so a 2-second note and a 2-minute note both
 * produce a chip with this many bars.
 */
const PEAK_BAR_COUNT = 28;
/**
 * Cadence for the chip-preview peak sampler. Smaller values produce
 * a higher-resolution `peaks` array before downsampling (more accurate
 * waveform shape on stop); larger values keep memory bounded for very
 * long recordings. 100ms is a good middle ground — at 60 seconds of
 * audio that's 600 samples downsampled to 28, plenty of detail for
 * the chip's footprint without retaining a huge buffer.
 */
const PEAK_SAMPLE_INTERVAL_MS = 100;
/**
 * FFT size for the AnalyserNode. Larger values trade CPU for finer
 * time-domain resolution; 2048 produces a 1024-sample frame at the
 * standard 48kHz rate, which is ~21ms of audio — plenty for amplitude
 * detection without needlessly burning frames on per-pixel waveform
 * accuracy (we're rendering coarse bars, not a continuous wave).
 */
const ANALYSER_FFT_SIZE = 2048;
export function useVoiceRecorder(options) {
    const [isRecording, setIsRecording] = useState(false);
    const [durationSeconds, setDurationSeconds] = useState(0);
    const [liveLevels, setLiveLevels] = useState([]);
    // Stash the live `onComplete` so MediaRecorder's `onstop` (captured
    // at construction) always calls the freshest version without tearing
    // down + recreating the recorder on every render.
    const onCompleteRef = useRef(options.onComplete);
    useEffect(() => {
        onCompleteRef.current = options.onComplete;
    }, [options.onComplete]);
    // Resource handles. Held in refs so the cleanup path can tear them
    // down without triggering re-renders (and so the stop / cancel
    // callbacks can read them at call time without stale-closure risk).
    const mediaRecorderRef = useRef(null);
    const audioContextRef = useRef(null);
    const analyserRef = useRef(null);
    const streamRef = useRef(null);
    const chunksRef = useRef([]);
    const peaksRef = useRef([]);
    const startTimeRef = useRef(0);
    const durationTimerRef = useRef(null);
    const rafRef = useRef(null);
    // Flag set by `cancel()` so the next `onstop` skips the completion
    // callback. Cleared after the teardown so a subsequent start cycle
    // doesn't inherit a stale cancel flag.
    const cancelledRef = useRef(false);
    // Tear down all per-recording resources. Idempotent — safe to call
    // multiple times (e.g. from both the natural stop path and the
    // unmount cleanup).
    const teardown = useCallback(() => {
        const stream = streamRef.current;
        if (stream !== null) {
            stream.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
        }
        const audioContext = audioContextRef.current;
        if (audioContext !== null) {
            // AudioContext.close() is async but we don't need to await — the
            // browser handles the final teardown in the background. Catch
            // any rejection silently because the context might already be
            // closing when this runs (cancel during stop, for example).
            audioContext.close().catch(() => {
                /* already closed */
            });
            audioContextRef.current = null;
        }
        analyserRef.current = null;
        mediaRecorderRef.current = null;
        if (durationTimerRef.current !== null) {
            window.clearInterval(durationTimerRef.current);
            durationTimerRef.current = null;
        }
        if (rafRef.current !== null) {
            window.cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
    }, []);
    const start = useCallback(async () => {
        if (typeof navigator === "undefined" ||
            navigator.mediaDevices === undefined ||
            typeof MediaRecorder === "undefined") {
            throw new Error("Voice recording is not supported in this browser");
        }
        const stream = await navigator.mediaDevices.getUserMedia({
            audio: true,
            video: false,
        });
        streamRef.current = stream;
        // AudioContext branch — feeds the analyser only. The MediaRecorder
        // pulls its data straight from the stream, so the analyser path
        // is observation-only and can't perturb the recorded audio.
        const AudioCtor = window.AudioContext ??
            window
                .webkitAudioContext;
        if (AudioCtor === undefined) {
            // Lack of an AudioContext doesn't prevent recording, but it
            // does prevent the live amplitude meter from updating. Bail
            // cleanly so the caller doesn't end up with a half-initialised
            // recorder.
            stream.getTracks().forEach((track) => track.stop());
            throw new Error("Audio analysis is not supported in this browser");
        }
        const audioContext = new AudioCtor();
        audioContextRef.current = audioContext;
        const source = audioContext.createMediaStreamSource(stream);
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = ANALYSER_FFT_SIZE;
        source.connect(analyser);
        analyserRef.current = analyser;
        const mediaRecorder = new MediaRecorder(stream);
        mediaRecorderRef.current = mediaRecorder;
        chunksRef.current = [];
        peaksRef.current = [];
        cancelledRef.current = false;
        mediaRecorder.ondataavailable = (event) => {
            if (event.data.size > 0) {
                chunksRef.current.push(event.data);
            }
        };
        mediaRecorder.onstop = () => {
            const finalDurationMs = Date.now() - startTimeRef.current;
            const wasCancelled = cancelledRef.current;
            const collectedPeaks = peaksRef.current;
            const collectedChunks = chunksRef.current;
            const mimeType = mediaRecorder.mimeType || "audio/webm";
            teardown();
            setIsRecording(false);
            setDurationSeconds(0);
            setLiveLevels([]);
            if (wasCancelled) {
                cancelledRef.current = false;
                return;
            }
            // Avoid emitting an empty blob — happens if the user taps
            // start and immediately cancels via the OS-level prompt, or
            // if `ondataavailable` never fired (some browsers buffer
            // until stop and then drop the data when the stream tears
            // down too quickly).
            const totalSize = collectedChunks.reduce((acc, c) => acc + c.size, 0);
            if (totalSize === 0)
                return;
            const blob = new Blob(collectedChunks, { type: mimeType });
            const extension = mimeType.includes("mp4") ? "m4a" : "webm";
            const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
            const file = new File([blob], `voice-${timestamp}.${extension}`, {
                type: mimeType,
            });
            const peaks = downsamplePeaks(collectedPeaks, PEAK_BAR_COUNT);
            onCompleteRef.current(file, peaks, finalDurationMs / 1000);
        };
        startTimeRef.current = Date.now();
        // `start(100)` keeps `ondataavailable` firing every ~100ms so a
        // long recording's blob can be assembled incrementally instead
        // of stalling the main thread on the final `stop`.
        mediaRecorder.start(100);
        setIsRecording(true);
        // Duration ticker — separate from the analyser loop so the
        // displayed seconds stay accurate even if the rAF loop misses a
        // frame (e.g. tab backgrounded briefly).
        durationTimerRef.current = window.setInterval(() => {
            setDurationSeconds((Date.now() - startTimeRef.current) / 1000);
        }, 100);
        // Analyser loop — samples the time-domain buffer once per frame,
        // computes a peak amplitude (deviation from the 128 silence
        // baseline, normalised to 0..1), and feeds two consumers:
        //   • the live meter (rolling window of last N values)
        //   • the peak sampler for the chip preview (one value every
        //     PEAK_SAMPLE_INTERVAL_MS)
        const buffer = new Uint8Array(analyser.fftSize);
        // Live window kept in a closure-local array so we can mutate +
        // re-publish to React state without bouncing through a ref.
        let liveWindow = [];
        let lastPeakAt = startTimeRef.current;
        const tick = () => {
            const live = analyserRef.current;
            if (live === null)
                return;
            live.getByteTimeDomainData(buffer);
            // Peak amplitude — strongest deviation from the 128 silence
            // midpoint in this frame. More responsive than RMS for spotting
            // word boundaries / breaths, which is what the live meter
            // really wants to surface (visual feedback that the mic is
            // "hearing" the user).
            let maxAmplitude = 0;
            for (let i = 0; i < buffer.length; i++) {
                const v = Math.abs(buffer[i] - 128) / 128;
                if (v > maxAmplitude)
                    maxAmplitude = v;
            }
            // Live meter update — shift left, push new value, publish.
            liveWindow.push(maxAmplitude);
            if (liveWindow.length > LIVE_BAR_COUNT) {
                liveWindow = liveWindow.slice(liveWindow.length - LIVE_BAR_COUNT);
            }
            // Pad with leading silence so the meter always has the full
            // LIVE_BAR_COUNT bars rendered (no jumpy fill-from-the-right
            // during the first second of recording).
            const padded = liveWindow.length < LIVE_BAR_COUNT
                ? new Array(LIVE_BAR_COUNT - liveWindow.length).fill(0).concat(liveWindow)
                : liveWindow;
            setLiveLevels(padded.slice());
            // Chip-preview peak sampler — append one value per interval.
            const now = Date.now();
            if (now - lastPeakAt >= PEAK_SAMPLE_INTERVAL_MS) {
                peaksRef.current.push(maxAmplitude);
                lastPeakAt = now;
            }
            rafRef.current = window.requestAnimationFrame(tick);
        };
        rafRef.current = window.requestAnimationFrame(tick);
    }, [teardown]);
    const stop = useCallback(() => {
        const recorder = mediaRecorderRef.current;
        if (recorder === null || recorder.state === "inactive")
            return;
        // Don't optimistically flip `isRecording` here — the `onstop`
        // handler is what tears the state down, and flipping early would
        // cause the live meter to disappear before the final frame
        // assembles (visible as a one-frame flicker of empty input row
        // chrome). The MediaRecorder's `stop()` is synchronous enough
        // that this is invisible to the user.
        recorder.stop();
    }, []);
    const cancel = useCallback(() => {
        const recorder = mediaRecorderRef.current;
        if (recorder === null || recorder.state === "inactive")
            return;
        cancelledRef.current = true;
        recorder.stop();
    }, []);
    // Belt-and-braces unmount cleanup. The natural `onstop` path tears
    // resources down too, but if the omnibar host unmounts while the
    // user is mid-recording (rare — auth boundary, route swap with a
    // teardown) the recorder won't get a chance to emit `onstop`
    // before the component is gone. Calling `cancel()` here forces the
    // teardown path synchronously so the browser's mic indicator never
    // outlives the component.
    useEffect(() => {
        return () => {
            cancelledRef.current = true;
            const recorder = mediaRecorderRef.current;
            if (recorder !== null && recorder.state !== "inactive") {
                recorder.stop();
            }
            teardown();
        };
    }, [teardown]);
    return {
        isRecording,
        durationSeconds,
        liveLevels,
        start,
        stop,
        cancel,
    };
}
/**
 * Reduce a raw amplitude sample array to a fixed-length bar list via
 * max-bucketing. Each output bar represents the loudest peak in its
 * input slice — preserves the perceived "shape" of the waveform (where
 * speech bursts vs silences sat) far better than averaging would.
 *
 * Short recordings (`samples.length < targetCount`) are padded with
 * trailing zeros so the renderer can always assume a fixed bar count.
 * Empty input returns an all-zero array.
 */
function downsamplePeaks(samples, targetCount) {
    if (targetCount <= 0)
        return [];
    if (samples.length === 0)
        return new Array(targetCount).fill(0);
    if (samples.length <= targetCount) {
        const padded = samples.slice();
        while (padded.length < targetCount)
            padded.push(0);
        return padded;
    }
    const bucketSize = samples.length / targetCount;
    const result = [];
    for (let i = 0; i < targetCount; i++) {
        const start = Math.floor(i * bucketSize);
        const end = Math.floor((i + 1) * bucketSize);
        let max = 0;
        for (let j = start; j < end; j++) {
            const v = samples[j];
            if (v > max)
                max = v;
        }
        result.push(max);
    }
    return result;
}
